


import React, { useEffect, useMemo, useState } from 'react';
import { CONFIG } from 'config/config';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
	NavigationWrapper,
	PickerHOC,
	Text,
	UiWrapper,
	UserWrapper,
	View,
} from '@coinscrap/webapp-core';

import { Wrapper } from 'layout/components/Body/Wrapper';
import Footer from 'layout/components/Footer/Footer';
import Header from 'layout/components/Header/Header';

import BackNextButton from 'components/BackNextButton/BackNextButton';
import InputLabel from 'components/InputLabel/InputLabel';
import { MainModal } from 'components/MainModal/MainModal';

import { useGetError } from 'hooks/useGetError';
import { useInitialScrollToTop } from 'hooks/useInitialScrollToTop';

export const IdentityBiometricSelect = PickerHOC({
	user: {
		sourcePath: 'user',
		sourceWrapper: UserWrapper,
	},
})(() => {
	const { user } = PickerHOC.use();
	const { updateUser } = UserWrapper.use();
	const { useHeader, useFooter, useLoading, openModal } = UiWrapper.use();
	const { navigateTo, goBack, params } = NavigationWrapper.use();
	const { t, i18n } = useTranslation();

	const [loading, setLoading] = useState();

	useFooter(<Footer />, []);
	useHeader(<Header />, []);
	useLoading(loading, { loadingMessage: loading });
	useInitialScrollToTop();
	const getError = useGetError();

	// ESTADO
	const selectType = useMemo(
		() =>
			params?.selectType && params?.selectType !== ':selectType'
				? params?.selectType
				: 'documentCountry',
		[params],
	);

	const [documentCountry, setDocumentCountry] = useState(
		user?.metadata?.documentCountryIDS?.trim() || 'esp',
	);
	const [documentType, setDocumentType] = useState(
		user?.metadata?.documentTypeIDS?.trim() || 'dni',
	);

	// NAVIGATION
	const next = async () => {
		switch (selectType) {
			case 'documentCountry':
				try {
					setLoading('Guardando...');
					await updateUser({
						metadata: {
							...user?.metadata,
							documentCountryIDS: documentCountry,
							documentCountryIDSName: CONFIG.views.identityBiometrics.steps.selectDocumentType.validDocumentCountries.find(
								(country) => country.value === documentCountry,
							).label,
						},
					});
					setLoading('Empezando el proceso...');
					CONFIG.routing.identityBiometricSelect.next(navigateTo);
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-IdentityBiometricSelect-KO',
					});
				}
				break;
			case 'documentType':
				try {
					setLoading('Guardando...');
					await updateUser({
						metadata: {
							...user?.metadata,
							documentTypeIDS: documentType,
							documentTypeIDSName: CONFIG.views.identityBiometrics.steps.selectDocumentType.validDocumentTypes.find(
								(doc) => doc.value === documentType,
							).label,
						},
					});
					setLoading('Empezando el proceso...');
					CONFIG.routing.identityBiometricSelect.next(navigateTo, selectType);
				} catch (e) {
					setLoading(undefined);
					openModal(() => <MainModal type="error" text={getError(e)} />, {
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-IdentityBiometricSelect-KO',
					});
				}
				break;
			default:
				setLoading(undefined);
				openModal(
					() => (
						<MainModal
							type="error"
							text={getError(
								new Error(`selectType no válido: selectType ${selectType}`),
							)}
						/>
					),
					{
						disableClickAway: true,
						closeButton: true,
						modalClass: isMobile ? 'modal-xl' : 'modal-main',
						key: 'modal-ClientData-defaultKO',
					},
				);
				break;
		}
	};

	// EFFECTS
	useEffect(() => {
		console.log('📌 user', user);
		setLoading(undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [params]);

	return (
		<Wrapper showTopSection step={3} title="Documento de identidad">
			<View innerWrapperSt>
				{
					{
						// PAÍS DEL DOCUMENTO
						documentCountry: (
							<>
								<Text headingTertiarySt>
									Selecciona el país de tu documento de identidad
								</Text>
								<InputLabel
									value={documentCountry || 0}
									onChange={(e) => {
										setDocumentCountry(e.target.value);
									}}
									label="País del documento"
									type="selectCountry"
									selectOptions={CONFIG.views.identityBiometrics.steps.selectDocumentType.validDocumentCountries.map(
										(item) => {
											return {
												label: item.label,
												value: item.value,
												alpha2: item.alpha2,
											};
										},
									)}
								/>
							</>
						),
						// TIPO DEL DOCUMENTO
						documentType: (
							<>
								<Text headingTertiarySt>
									Selecciona el tipo de tu documento de identidad
								</Text>
								<InputLabel
									value={documentType || 0}
									onChange={(e) => {
										setDocumentType(e.target.value);
									}}
									label="Tipo de documento"
									type="select"
									selectOptions={CONFIG.views.identityBiometrics.steps.selectDocumentType.validDocumentTypes.map(
										(item) => {
											return {
												label: item.label,
												value: item.value,
											};
										},
									)}
								/>
							</>
						),
					}[selectType]
				}
			</View>
			<BackNextButton isBackShown={false} onClickNext={next} />
		</Wrapper>
	);
});
